import { AffineContext } from '@affine/component/context';
import { GlobalLoading } from '@affine/component/global-loading';
import { AppFallback } from '@affine/core/components/affine/app-container';
import { QueryClientProvider } from '@affine/core/components/providers/query-client-provider';
import { SyncProvider } from '@affine/core/components/providers/sync-provider';
import { UpdateVersionProvider } from '@affine/core/components/providers/update-version-provider';
import { YandexMetricProvider } from '@affine/core/components/providers/yandex-metric-provider';
import { router } from '@affine/core/desktop/router';
import { configureCommonModules } from '@affine/core/modules';
import { setup as setupDay } from '@affine/core/modules/day';
import { configureLocalStorageStateStorageImpls } from '@affine/core/modules/storage';
import { Telemetry } from '@affine/core/modules/telemetry';
import { CustomThemeModifier } from '@affine/core/modules/theme-editor';
import { configureIndexedDBUserspaceStorageProvider } from '@affine/core/modules/userspace';
import { configureBrowserWorkbenchModule } from '@affine/core/modules/workbench';
import {
  configureBrowserWorkspaceFlavours,
  configureIndexedDBWorkspaceEngineStorageProvider,
} from '@affine/core/modules/workspace-engine';
import createEmotionCache from '@affine/core/utils/create-emotion-cache';
import { createI18n, setUpLanguage } from '@affine/i18n';
import { setup as setupSync } from '@docnboard/sync';
import { CacheProvider } from '@emotion/react';
import {
  Framework,
  FrameworkRoot,
  getCurrentStore,
  LifecycleService,
} from '@toeverything/infra';
import { Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';

const cache = createEmotionCache();

const future = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

async function loadLanguage() {
  const i18n = createI18n();
  document.documentElement.lang = i18n.language;

  await setUpLanguage(i18n);
}

let setupSyncPromise: Promise<void> | null = null;
let languageLoadingPromise: Promise<void> | null = null;

const framework = new Framework();
configureCommonModules(framework);
configureBrowserWorkbenchModule(framework);
configureLocalStorageStateStorageImpls(framework);
configureBrowserWorkspaceFlavours(framework);
configureIndexedDBWorkspaceEngineStorageProvider(framework);
configureIndexedDBUserspaceStorageProvider(framework);
const frameworkProvider = framework.provider();

setupDay();

// setup application lifecycle events, and emit application start event
window.addEventListener('focus', () => {
  frameworkProvider.get(LifecycleService).applicationFocus();
});
frameworkProvider.get(LifecycleService).applicationStart();

export function App() {
  if (!languageLoadingPromise) {
    languageLoadingPromise = loadLanguage().catch(console.error);
  }

  if (!setupSyncPromise) {
    setupSyncPromise = setupSync().catch(console.error);
  }

  return (
    <Suspense>
      <FrameworkRoot framework={frameworkProvider}>
        <CacheProvider value={cache}>
          <SyncProvider>
            <QueryClientProvider>
              <AffineContext store={getCurrentStore()}>
                <Telemetry />
                <CustomThemeModifier />
                <YandexMetricProvider />
                <GlobalLoading />
                <UpdateVersionProvider />
                <RouterProvider
                  fallbackElement={<AppFallback key="RouterFallback" />}
                  router={router}
                  future={future}
                />
              </AffineContext>
            </QueryClientProvider>
          </SyncProvider>
        </CacheProvider>
      </FrameworkRoot>
    </Suspense>
  );
}
